import * as React from "react";
import { useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

import {
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PublishIcon from "@material-ui/icons/Publish";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import TableChartIcon from "@material-ui/icons/TableChart";

import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";

const MAX_FILE_BYTES = 1 * 1024 * 1024;

/** Columns shown in the results table and Excel export */
const RESULT_COLUMNS = [
    { key: "row", label: "#" },
    { key: "code", label: "Promo Code" },
    { key: "affiliate_name", label: "Affiliate Name" },
    { key: "amount", label: "Amount (£)" },
    { key: "password", label: "Affiliate Password" },
] as const;

type ResultColumnKey = typeof RESULT_COLUMNS[number]["key"];

const useStyles = makeStyles((theme) => ({
    card: {
        marginBottom: theme.spacing(3),
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "visible",
    },
    cardContent: {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "visible",
        boxSizing: "border-box",
        "&:last-child": {
            paddingBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
        gap: theme.spacing(1),
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
    },
    cardIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    dropZone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(4, 2),
        textAlign: "center",
        backgroundColor: "#fafafa",
        cursor: "pointer",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "rgba(218, 41, 114, 0.04)",
        },
    },
    dropZoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: "rgba(218, 41, 114, 0.06)",
    },
    fileName: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        fontWeight: 600,
    },
    headerRight: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        flexShrink: 0,
        marginLeft: "auto",
    },
    sampleDownload: {
        textTransform: "none",
        fontWeight: 600,
    },
    actions: {
        padding: theme.spacing(2, 0, 0),
    },
    hiddenInput: {
        display: "none",
    },
    successBanner: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(2),
        background:
            "linear-gradient(135deg, rgba(218, 41, 114, 0.1), rgba(255, 255, 255, 0.98))",
        border: "1px solid rgba(218, 41, 114, 0.2)",
        marginBottom: theme.spacing(2),
        boxShadow: "0 8px 24px rgba(218, 41, 114, 0.08)",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
    },
    successIcon: {
        color: theme.palette.primary.main,
        fontSize: 36,
        marginTop: 2,
    },
    summaryChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    sheetPanel: {
        borderRadius: theme.spacing(2),
        border: "1px solid rgba(28, 30, 31, 0.1)",
        overflow: "hidden",
        boxShadow: "0 12px 40px rgba(28, 30, 31, 0.08)",
        background: "#fff",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
    },
    sheetToolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        background:
            "linear-gradient(90deg, rgba(218, 41, 114, 0.06), rgba(255, 255, 255, 0.95))",
        borderBottom: "1px solid rgba(28, 30, 31, 0.08)",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
    },
    sheetTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        fontWeight: 700,
        fontSize: "0.95rem",
    },
    sheetActions: {
        display: "flex",
        gap: theme.spacing(1),
        flexWrap: "wrap",
    },
    tableWrap: {
        maxHeight: "min(55vh, 520px)",
        overflowX: "auto",
        overflowY: "auto",
        width: "100%",
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
    },
    table: {
        width: "100%",
        tableLayout: "fixed",
    },
    headCell: {
        background: "linear-gradient(180deg, #f8f4f6 0%, #efe8ec 100%)",
        color: "#1c1e1f",
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        borderBottom: "2px solid rgba(218, 41, 114, 0.25)",
        padding: "12px 14px",
        position: "sticky",
        top: 0,
        zIndex: 2,
    },
    rowNumCell: {
        backgroundColor: "#f5f5f5",
        fontWeight: 700,
        color: "#666",
        width: 48,
        textAlign: "center",
        borderRight: "1px solid rgba(28, 30, 31, 0.08)",
    },
    bodyCell: {
        fontSize: "0.84rem",
        padding: "10px 14px",
        borderBottom: "1px solid rgba(28, 30, 31, 0.06)",
        whiteSpace: "nowrap",
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    zebraRow: {
        "&:nth-of-type(even)": {
            backgroundColor: "rgba(248, 246, 243, 0.7)",
        },
        "&:hover": {
            backgroundColor: "rgba(218, 41, 114, 0.05)",
        },
    },
    codeCell: {
        fontFamily: '"Space Grotesk", "Manrope", sans-serif',
        fontWeight: 700,
        color: theme.palette.primary.main,
        letterSpacing: "0.04em",
    },
    codeCellFailed: {
        fontFamily: '"Space Grotesk", "Manrope", sans-serif',
        fontWeight: 700,
        color: "#c62828",
        letterSpacing: "0.04em",
        textDecoration: "line-through",
        opacity: 0.85,
    },
    failureMeta: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
        whiteSpace: "normal",
    },
    failureChip: {
        backgroundColor: "rgba(244, 67, 54, 0.12)",
        color: "#c62828",
        fontWeight: 700,
        height: 22,
        fontSize: "0.72rem",
    },
    duplicateChip: {
        backgroundColor: "rgba(255, 152, 0, 0.14)",
        color: "#e65100",
        fontWeight: 700,
        height: 22,
        fontSize: "0.72rem",
    },
    failureCaption: {
        color: "#c62828",
        fontSize: "0.75rem",
        lineHeight: 1.35,
        maxWidth: 240,
        whiteSpace: "normal",
    },
    failedTableRow: {
        backgroundColor: "rgba(244, 67, 54, 0.05) !important",
        boxShadow: "inset 3px 0 0 #ef5350",
        "&:hover": {
            backgroundColor: "rgba(244, 67, 54, 0.08) !important",
        },
    },
    failuresSummary: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.75, 2),
        borderRadius: theme.spacing(1.5),
        border: "1px solid rgba(244, 67, 54, 0.25)",
        backgroundColor: "rgba(244, 67, 54, 0.04)",
    },
    failureSummaryItem: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    passwordCell: {
        fontFamily: "monospace",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 4,
    },
}));

type CreatedItem = {
    row?: number;
    code?: string;
    message?: string;
    password?: string;
    note?: string;
    data?: {
        code?: string;
        password?: string;
        note?: string;
        message?: string;
    };
};

type FailedItem = {
    row?: number;
    code?: string;
    error?: string;
};

type BulkResult = {
    message?: string;
    total?: number;
    success_count?: number;
    failure_count?: number;
    created?: CreatedItem[];
    failed?: FailedItem[];
};

type SourceRow = {
    row: number;
    code: string;
    amount: string;
    from_date: string;
    to_date: string;
    sws_introducer: string;
    remortgage_introducer: string;
    direct_debit_introducer: string;
    affiliate_name: string;
    generate_password: string;
};

type SpreadsheetRow = SourceRow & {
    password: string;
    status: string;
    message: string;
    failureKind: "duplicate" | "other" | null;
};

const SAMPLE_CSV_FILENAME = "promos-random-10-more.csv";
const SAMPLE_CSV_URL = `${process.env.PUBLIC_URL}/${SAMPLE_CSV_FILENAME}`;

const splitCsvLine = (line: string): string[] => {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }
        if (ch === "," && !inQuotes) {
            cells.push(current.trim());
            current = "";
            continue;
        }
        current += ch;
    }
    cells.push(current.trim());
    return cells;
};

const parseCsvText = (text: string): { rows: SourceRow[]; headers: string[] } => {
    const lines = text
        .replace(/^\ufeff/, "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

    if (lines.length < 2) {
        return { rows: [], headers: [] };
    }

    const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
    const idx = (name: string) => headers.indexOf(name);

    const rows = lines.slice(1).map((line, index) => {
        const cells = splitCsvLine(line);
        const get = (name: string) => {
            const i = idx(name);
            return i >= 0 ? cells[i] || "" : "";
        };
        return {
            row: index + 2,
            code: get("code"),
            amount: get("amount"),
            from_date: get("from_date"),
            to_date: get("to_date"),
            sws_introducer: get("sws_introducer"),
            remortgage_introducer: get("remortgage_introducer"),
            direct_debit_introducer: get("direct_debit_introducer"),
            affiliate_name: get("affiliate_name"),
            generate_password: get("generate_password"),
        };
    });

    return { rows, headers };
};

const getAffiliateNameValidationError = (headers: string[], rows: SourceRow[]): string | null => {
    if (headers.indexOf("affiliate_name") < 0) {
        return 'CSV must include an "affiliate_name" column.';
    }

    const missingRows = rows.filter((row) => !row.affiliate_name.trim());
    if (!missingRows.length) {
        return null;
    }

    const preview = missingRows
        .slice(0, 5)
        .map((row) => row.row)
        .join(", ");
    const extra = missingRows.length > 5 ? ` (+${missingRows.length - 5} more)` : "";

    return `affiliate_name is required on every row. Missing on row${
        missingRows.length > 1 ? "s" : ""
    } ${preview}${extra}.`;
};

const readFileAsText = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsText(file);
    });

const isDuplicateError = (message: string) => {
    const normalized = message.toLowerCase();
    return (
        normalized.includes("duplicate") ||
        normalized.includes("already exist") ||
        normalized.includes("unique constraint") ||
        normalized.includes("must be unique") ||
        normalized.includes("er_dup_entry") ||
        normalized.includes("already been taken")
    );
};

const formatFailureMessage = (error: string, code: string) => {
    const trimmed = error.trim();
    if (!trimmed) {
        return "Failed to create promo code";
    }

    if (!isDuplicateError(trimmed)) {
        return trimmed;
    }

    if (trimmed.toLowerCase().includes("duplicate code in csv")) {
        return `Duplicate in CSV — ${code || "this code"} appears more than once in the file`;
    }

    return `Duplicate promo code — ${code || "This code"} already exists`;
};

const getFailureKind = (message: string): "duplicate" | "other" =>
    isDuplicateError(message) ? "duplicate" : "other";

const normalizeCreatedItem = (item: CreatedItem) => {
    const code = item.code || item.data?.code || "—";
    const password = item.password || item.data?.password || "";
    const message = item.message || item.data?.message || "Promo code created";
    return { code, password, message, row: item.row };
};

const buildSpreadsheetRows = (
    sourceRows: SourceRow[],
    result: BulkResult
): SpreadsheetRow[] => {
    const createdByRow = new Map(
        (result.created || []).map((item) => [item.row, normalizeCreatedItem(item)])
    );
    const failedByRow = new Map((result.failed || []).map((item) => [item.row, item]));

    if (sourceRows.length) {
        return sourceRows.map((source) => {
            const created = createdByRow.get(source.row);
            const failed = failedByRow.get(source.row);
            const failureMessage = failed
                ? formatFailureMessage(failed.error || "", source.code)
                : "";
            return {
                ...source,
                password: created?.password || "",
                status: failed ? "Failed" : created ? "Created" : "—",
                message: failed ? failureMessage : created?.message || "",
                failureKind: failed ? getFailureKind(failed.error || failureMessage) : null,
            };
        });
    }

    const rows: SpreadsheetRow[] = [];
    (result.created || []).forEach((item) => {
        const c = normalizeCreatedItem(item);
        rows.push({
            row: c.row || 0,
            code: c.code,
            amount: "",
            from_date: "",
            to_date: "",
            sws_introducer: "",
            remortgage_introducer: "",
            direct_debit_introducer: "",
            affiliate_name: "",
            generate_password: "",
            password: c.password,
            status: "Created",
            message: c.message,
            failureKind: null,
        });
    });
    (result.failed || []).forEach((item) => {
        const failureMessage = formatFailureMessage(item.error || "", item.code || "");
        rows.push({
            row: item.row || 0,
            code: item.code || "",
            amount: "",
            from_date: "",
            to_date: "",
            sws_introducer: "",
            remortgage_introducer: "",
            direct_debit_introducer: "",
            affiliate_name: "",
            generate_password: "",
            password: "",
            status: "Failed",
            message: failureMessage,
            failureKind: getFailureKind(item.error || failureMessage),
        });
    });
    return rows.sort((a, b) => a.row - b.row);
};

const toExcelRows = (rows: SpreadsheetRow[]) =>
    rows.map((row) => ({
        "Promo Code": row.code,
        "Affiliate Name": row.affiliate_name || "—",
        "Amount (£)": row.amount || "—",
        "Affiliate Password": row.password || "—",
    }));

const BulkCreatePromocode = () => {
    const classes = useStyles();
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [file, setFile] = useState<File | null>(null);
    const [sourceRows, setSourceRows] = useState<SourceRow[]>([]);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [result, setResult] = useState<BulkResult | null>(null);

    const spreadsheetRows = useMemo(
        () => (result ? buildSpreadsheetRows(sourceRows, result) : []),
        [result, sourceRows]
    );

    const failedRows = useMemo(
        () => spreadsheetRows.filter((row) => row.status === "Failed"),
        [spreadsheetRows]
    );

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleReset = () => {
        setResult(null);
        setFile(null);
        setSourceRows([]);
        resetFileInput();
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        notify(`${label} copied to clipboard`, "info");
    };

    const downloadExcel = () => {
        if (!spreadsheetRows.length) {
            notify("No data to export", "warning");
            return;
        }

        const ws = XLSX.utils.json_to_sheet(toExcelRows(spreadsheetRows));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Bulk Promo Codes");

        const colWidths = [
            { wch: 18 },
            { wch: 22 },
            { wch: 12 },
            { wch: 18 },
            { wch: 12 },
        ];
        ws["!cols"] = colWidths;

        const stamp = new Date().toISOString().split("T")[0];
        XLSX.writeFile(wb, `bulk_promo_codes_${stamp}.xlsx`);
        notify("Excel file downloaded", "info");
    };

    const validateAndSetFile = async (nextFile: File | null) => {
        if (!nextFile) {
            setFile(null);
            setSourceRows([]);
            return;
        }

        const isCsv =
            /\.csv$/i.test(nextFile.name) ||
            nextFile.type === "text/csv" ||
            nextFile.type === "application/csv" ||
            nextFile.type === "text/plain";

        if (!isCsv) {
            notify("Please select a .csv file", "warning");
            resetFileInput();
            setFile(null);
            setSourceRows([]);
            return;
        }

        if (nextFile.size > MAX_FILE_BYTES) {
            notify("CSV exceeds the 1MB limit", "warning");
            resetFileInput();
            setFile(null);
            setSourceRows([]);
            return;
        }

        try {
            const text = await readFileAsText(nextFile);
            const { rows, headers } = parseCsvText(text);
            if (!rows.length) {
                notify("CSV must have a header row and at least one data row", "warning");
                resetFileInput();
                setFile(null);
                setSourceRows([]);
                return;
            }

            const affiliateNameError = getAffiliateNameValidationError(headers, rows);
            if (affiliateNameError) {
                notify(affiliateNameError, "warning");
                resetFileInput();
                setFile(null);
                setSourceRows([]);
                return;
            }

            setFile(nextFile);
            setSourceRows(rows);
        } catch {
            notify("Could not read CSV file", "error");
            resetFileInput();
            setFile(null);
            setSourceRows([]);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files && event.target.files[0];
        validateAndSetFile(selected || null);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        const dropped = event.dataTransfer.files && event.dataTransfer.files[0];
        validateAndSetFile(dropped || null);
    };

    const handleUpload = async () => {
        if (!file) {
            notify("Please choose a CSV file first", "warning");
            return;
        }

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const accessToken = localStorage.getItem("accessToken") || "";
            const response = await axios.post(ENDPOINTS.BULK_CREATE_PROMOCODE, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const data = (response.data || {}) as BulkResult;
            setResult(data);

            const successCount = data.success_count ?? 0;
            const failureCount = data.failure_count ?? 0;
            notify(
                data.message ||
                    `Bulk create completed — ${successCount} created, ${failureCount} failed`,
                failureCount > 0 && successCount === 0 ? "warning" : "info",
                "",
                undefined,
                8000
            );

            setFile(null);
            resetFileInput();
        } catch (error: any) {
            notify(
                error?.response?.data?.message || "Failed to bulk create promo codes",
                "error",
                "",
                undefined,
                50000
            );
        } finally {
            setUploading(false);
        }
    };

    const renderCellValue = (row: SpreadsheetRow, key: ResultColumnKey) => {
        if (key === "row") {
            return row.row;
        }

        const value = (row[key as keyof SpreadsheetRow] as string) || "—";

        if (key === "code") {
            if (row.status === "Failed") {
                const isDuplicate = row.failureKind === "duplicate";
                return (
                    <Box>
                        <span className={classes.codeCellFailed}>{value}</span>
                        <Box className={classes.failureMeta}>
                            <Chip
                                size="small"
                                label={isDuplicate ? "Duplicate promo code" : "Failed"}
                                className={isDuplicate ? classes.duplicateChip : classes.failureChip}
                            />
                            <Typography className={classes.failureCaption}>
                                {row.message}
                            </Typography>
                        </Box>
                    </Box>
                );
            }

            return <span className={classes.codeCell}>{value}</span>;
        }

        if (key === "password" && row.password) {
            return (
                <Box className={classes.passwordCell}>
                    <span>{row.password}</span>
                    <Tooltip title="Copy password">
                        <IconButton
                            size="small"
                            onClick={() => copyToClipboard(row.password, "Password")}
                        >
                            <FileCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }

        return value;
    };

    return (
        <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
                <div className={classes.cardHeader}>
                    <div className={classes.headerLeft}>
                        <CloudUploadIcon className={classes.cardIcon} />
                        <Typography variant="h6">
                            {result ? "Bulk Create — Results" : "Bulk Create Promo Codes"}
                        </Typography>
                    </div>
                    <div className={classes.headerRight}>
                        {!result && (
                            <Button
                                component="a"
                                href={SAMPLE_CSV_URL}
                                download={SAMPLE_CSV_FILENAME}
                                variant="text"
                                color="primary"
                                size="small"
                                className={classes.sampleDownload}
                                startIcon={<GetAppIcon />}
                            >
                                Here is sample CSV
                            </Button>
                        )}
                        {result && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={handleReset}
                            >
                                Upload another CSV
                            </Button>
                        )}
                    </div>
                </div>

                {!result ? (
                    <>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Upload a CSV file to create multiple promo codes at once.{" "}
                            <strong>affiliate_name</strong> is required on every row.
                        </Typography>

                        <input
                            ref={fileInputRef}
                            className={classes.hiddenInput}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleFileChange}
                            disabled={uploading}
                        />

                        <div
                            className={`${classes.dropZone} ${dragActive ? classes.dropZoneActive : ""}`}
                            onClick={() => !uploading && fileInputRef.current?.click()}
                            onDragEnter={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                setDragActive(false);
                            }}
                            onDrop={handleDrop}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    fileInputRef.current?.click();
                                }
                            }}
                        >
                            <PublishIcon color="action" style={{ fontSize: 40 }} />
                            <Typography variant="body1" style={{ marginTop: 8 }}>
                                {file
                                    ? "Click to choose a different CSV"
                                    : "Drop CSV here or click to browse"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Max 1MB · up to 500 rows
                            </Typography>
                            {file && (
                                <Typography className={classes.fileName} variant="body2">
                                    <InsertDriveFileIcon fontSize="small" color="primary" />
                                    {file.name} ({Math.max(1, Math.round(file.size / 1024))} KB)
                                    {sourceRows.length > 0 &&
                                        ` · ${sourceRows.length} row${sourceRows.length === 1 ? "" : "s"}`}
                                </Typography>
                            )}
                        </div>

                        <CardActions className={classes.actions}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={!file || uploading}
                                onClick={handleUpload}
                                startIcon={
                                    uploading ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <CloudUploadIcon />
                                    )
                                }
                            >
                                {uploading ? "Uploading..." : "Upload & Create Promo Codes"}
                            </Button>
                        </CardActions>
                    </>
                ) : (
                    <>
                        <div className={classes.successBanner}>
                            <CheckCircleIcon className={classes.successIcon} />
                            <Box flex={1}>
                                <Typography variant="h6" style={{ fontWeight: 700 }}>
                                    {result.message || "Bulk create completed"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    All generated promo codes are listed below — row by row, like a
                                    spreadsheet.
                                </Typography>
                                <div className={classes.summaryChips}>
                                    <Chip label={`Total: ${result.total ?? 0}`} size="small" />
                                    <Chip
                                        label={`Created: ${result.success_count ?? 0}`}
                                        size="small"
                                        color="primary"
                                    />
                                    {(result.failure_count ?? 0) > 0 && (
                                        <Chip
                                            label={`Failed: ${result.failure_count}`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                </div>
                            </Box>
                        </div>

                        {failedRows.length > 0 && (
                            <div className={classes.failuresSummary}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#c62828" }}>
                                    {failedRows.length} promo code
                                    {failedRows.length === 1 ? "" : "s"} could not be created
                                </Typography>
                                {failedRows.map((row, index) => (
                                    <div
                                        key={`failure-summary-${row.row}-${row.code}-${index}`}
                                        className={classes.failureSummaryItem}
                                    >
                                        <ErrorOutlineIcon color="error" fontSize="small" />
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                Row {row.row} · {row.code || "Unknown code"}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {row.message}
                                            </Typography>
                                        </Box>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!!spreadsheetRows.length && (
                            <div className={classes.sheetPanel}>
                                <div className={classes.sheetToolbar}>
                                    <Typography className={classes.sheetTitle}>
                                        <TableChartIcon color="primary" fontSize="small" />
                                        Generated promo codes
                                    </Typography>
                                    <div className={classes.sheetActions}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            startIcon={<GetAppIcon />}
                                            onClick={downloadExcel}
                                        >
                                            Download Excel
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<FileCopyIcon />}
                                            onClick={() =>
                                                copyToClipboard(
                                                    spreadsheetRows
                                                        .map((r) =>
                                                            [
                                                                r.code,
                                                                r.affiliate_name,
                                                                r.password,
                                                            ]
                                                                .filter(Boolean)
                                                                .join("\t")
                                                        )
                                                        .join("\n"),
                                                    "All promo details"
                                                )
                                            }
                                        >
                                            Copy all codes
                                        </Button>
                                    </div>
                                </div>

                                <TableContainer className={classes.tableWrap}>
                                    <Table stickyHeader size="small" className={classes.table}>
                                        <TableHead>
                                            <TableRow>
                                                {RESULT_COLUMNS.map((col) => (
                                                    <TableCell
                                                        key={col.key}
                                                        className={classes.headCell}
                                                        style={
                                                            col.key === "row"
                                                                ? { width: 48 }
                                                                : undefined
                                                        }
                                                    >
                                                        {col.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {spreadsheetRows.map((row, index) => (
                                                <TableRow
                                                    key={`${row.row}-${row.code}-${index}`}
                                                    className={
                                                        row.status === "Failed"
                                                            ? classes.failedTableRow
                                                            : classes.zebraRow
                                                    }
                                                >
                                                    {RESULT_COLUMNS.map((col) => (
                                                        <TableCell
                                                            key={col.key}
                                                            className={
                                                                col.key === "row"
                                                                    ? classes.rowNumCell
                                                                    : classes.bodyCell
                                                            }
                                                            style={
                                                                col.key === "code" && row.status === "Failed"
                                                                    ? { whiteSpace: "normal" }
                                                                    : undefined
                                                            }
                                                        >
                                                            {renderCellValue(row, col.key)}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default BulkCreatePromocode;
